/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

/**
 * The "precisam de você hoje" band above a board.
 *
 * A board answers "where is everything"; it does not answer "what should I
 * touch first", and a broker opening one had to read every column to find
 * out. Each group here is a count plus the one sentence that says why it
 * matters, and pressing it narrows the board to exactly those records.
 *
 * **Every count must be honest.** The band totals what the page already
 * holds, so it belongs only above a board whose page fetched the whole
 * registry. Over a server-paged board it would present a slice as a total,
 * which is the failure the attendance board's `searchTruncated` warning
 * exists to prevent — Atendimentos has no band for that reason.
 *
 * Presentational on purpose: it takes counts, never records. Deciding what
 * "needs you" means is the feature's business, not this component's.
 */

export interface TriageGroup {
  /** Stable key. Also the selected value and the testid suffix. */
  id: string;
  /** The count. Rendered large, in the group's tone. */
  count: number;
  /** What the count is, already localized ("com marco pendente"). */
  label: string;
  /** Why it matters, already localized. One short sentence. */
  hint: string;
  /** Token reference for the count and icon (`'var(--accent-cobre)'`). */
  tone: string;
  icon: ReactNode;
}

export interface TriageBandProps {
  groups: TriageGroup[];
  /** The selected group, or null when the board is unfiltered. */
  selected: string | null;
  /** Selects a group, or clears when the selected one is pressed again. */
  onSelect: (id: string | null) => void;
  testId?: string;
}

/**
 * One group button.
 *
 * @param props.group - The group to render.
 * @param props.isSelected - Whether it currently filters the board.
 * @param props.onToggle - Selects it, or clears when already selected.
 * @param props.testId - Stable selector.
 * @returns The rendered button.
 */
function TriageGroupButton({
  group, isSelected, onToggle, testId,
}: {
  group: TriageGroup;
  isSelected: boolean;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <UnstyledButton
      type="button"
      onClick={onToggle}
      aria-pressed={isSelected}
      data-testid={testId}
      data-selected={isSelected}
      className={cn(
        'flex flex-1 basis-[210px] cursor-pointer items-center gap-(--space-3)',
        'border-0 border-r border-(--border-ultra-subtle) p-(--space-3) text-left',
        'last:border-r-0 hover:bg-(--surface-hover)',
      )}
      style={isSelected
        ? { background: 'color-mix(in srgb, var(--seed-base) 5%, transparent)' }
        : undefined}
    >
      <div
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-(--radius-md)"
        style={{
          background: `color-mix(in srgb, ${group.tone} 14%, transparent)`,
          color: group.tone,
        }}
      >
        {group.icon}
      </div>

      <div className="flex min-w-0 flex-col gap-px">
        <div className="flex flex-wrap items-baseline gap-(--space-1-5)">
          <Text
            component="span"
            style={{ color: group.tone }}
            className="text-[length:var(--text-stat)] [font-weight:var(--font-bold)] leading-none tabular-nums"
          >
            {group.count}
          </Text>
          <Text
            component="span"
            className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary)"
          >
            {group.label}
          </Text>
        </div>
        <Text
          component="span"
          className="text-[length:var(--text-meta)] text-(--text-secondary)"
        >
          {group.hint}
        </Text>
      </div>
    </UnstyledButton>
  );
}

/**
 * The triage band.
 *
 * A group with nothing in it is still shown rather than hidden. A zero is a
 * fact the broker wants — "nothing is overdue" is the answer they came for —
 * and a band that changed shape as records moved would make the page
 * rearrange itself under the reader.
 *
 * @param props.groups - The groups, in reading order.
 * @param props.selected - The group currently narrowing the board.
 * @param props.onSelect - Receives the new selection, or null to clear.
 * @param props.testId - Stable selector for the band.
 * @returns The rendered band.
 */
export function TriageBand({ groups, selected, onSelect, testId }: TriageBandProps) {
  const { t } = useTranslation('common');
  const root = testId ?? 'triage-band';

  return (
    <div
      data-testid={root}
      className={cn(
        'overflow-hidden rounded-(--radius-lg) border border-(--border-subtle)',
        'bg-(--surface-raised) shadow-[var(--shadow-sm)]',
      )}
    >
      <div className="flex flex-wrap items-center gap-(--space-2) border-b border-(--border-ultra-subtle) px-(--space-4) py-(--space-2-5)">
        <Text
          component="span"
          className={cn(
            'text-[length:var(--text-meta)] uppercase [font-weight:var(--font-semibold)]',
            'tracking-(--tracking-wider) text-(--text-tertiary)',
          )}
        >
          {t('triage.title')}
        </Text>
        <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary-soft)">
          {t('triage.hint')}
        </Text>
        {selected && (
          <UnstyledButton
            type="button"
            onClick={() => onSelect(null)}
            data-testid={`${root}-clear`}
            className={cn(
              'ml-auto cursor-pointer bg-transparent p-0',
              'text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]',
              'text-(--seed-base) underline underline-offset-[3px]',
            )}
          >
            {t('triage.clear')}
          </UnstyledButton>
        )}
      </div>

      <div className="flex flex-wrap">
        {groups.map((group) => (
          <TriageGroupButton
            key={group.id}
            group={group}
            isSelected={selected === group.id}
            onToggle={() => onSelect(selected === group.id ? null : group.id)}
            testId={`${root}-${group.id}`}
          />
        ))}
      </div>
    </div>
  );
}

export default TriageBand;
